<template>
  <div class="autocomplete-container">
    <div class="autocomplete-input-wrapper">
      <label for="location-input" class="visually-hidden">
        {{ placeholder }}
      </label>
      <input
        id="location-input"
        v-model="searchTerm"
        class="autocomplete-input"
        type="text"
        :placeholder="placeholder"
        :aria-controls="listboxId"
        :aria-activedescendant="
          highlightedIndex >= 0
            ? listboxId + '-option-' + highlightedIndex
            : null
        "
        @input="onInput"
        @keydown.down.prevent="moveSelection(1)"
        @keydown.up.prevent="moveSelection(-1)"
        @keydown.enter.prevent="selectHighlighted"
        @blur="onBlur"
      />
      <button
        v-if="searchTerm"
        class="autocomplete-clear"
        aria-label="Feld leeren"
        type="button"
        tabindex="0"
        @mousedown.prevent="onClearClick"
      >
        <IconClose width="30" height="30" />
      </button>
    </div>
    <ul
      v-if="showSuggestions"
      :id="listboxId"
      class="autocomplete-items"
      role="listbox"
    >
      <li
        v-for="(result, idx) in results"
        :id="listboxId + '-option-' + idx"
        :key="result.formatted + result.lat + result.lon"
        :class="[
          'autocomplete-item',
          { '-selected': idx === highlightedIndex },
        ]"
        role="option"
        tabindex="0"
        :aria-selected="idx === highlightedIndex"
        @mousedown.prevent="selectResult(result)"
      >
        {{ result.formatted }}
      </li>
    </ul>
  </div>
</template>

<script>
import IconClose from "../Icons/IconClose.vue";

export default {
  name: "LocationAutocomplete",
  components: {
    IconClose,
  },
  props: {
    placeholder: {
      type: String,
      default: "Search for a location",
    },
    debounceDelay: {
      type: Number,
      default: 300,
    },
    minLength: {
      type: Number,
      default: 3,
    },
    apiUrl: {
      type: String,
      default: "http://localhost:4000/api/autocomplete",
    },
    language: {
      type: String,
      default: "",
    },
    clearInput: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "address-chosen",
    "suggestions-loaded",
    "address-cleared",
    "update:clearInput",
  ],
  data() {
    return {
      searchTerm: "",
      results: [],
      showSuggestions: false,
      highlightedIndex: -1,
      debounceTimeout: null,
    };
  },
  computed: {
    acceptLanguage() {
      return this.language || navigator.language || "en";
    },
    listboxId() {
      return `autocomplete-listbox-${this._uid}`;
    },
  },
  watch: {
    clearInput(newVal) {
      if (newVal) {
        this.clear();
      }
    },
  },
  methods: {
    fetchSuggestions(query) {
      const url = `${this.apiUrl}?q=${encodeURIComponent(query)}`;
      fetch(url, {
        headers: {
          "Accept-Language": this.acceptLanguage,
        },
      })
        .then((res) => res.json())
        .then((data) => {
          this.results = Array.isArray(data) ? data : [];
          this.showSuggestions = this.results.length > 0;
          this.highlightedIndex = -1;
          this.$emit("suggestions-loaded");
        })
        .catch(() => {
          this.results = [];
          this.showSuggestions = false;
        });
    },
    onInput() {
      const value = this.searchTerm.trim();
      if (!value) {
        this.results = [];
        this.showSuggestions = false;
        this.$emit("address-cleared");
        return;
      }
      if (value.length < this.minLength) {
        this.results = [];
        this.showSuggestions = false;
        return;
      }
      if (this.debounceTimeout) clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.fetchSuggestions(value);
      }, this.debounceDelay);
    },
    selectResult(result) {
      this.$emit("address-chosen", {
        lat: result.lat,
        lon: result.lon,
        location: result,
      });
      this.searchTerm = result.formatted;
      this.showSuggestions = false;
    },
    moveSelection(direction) {
      if (!this.showSuggestions || this.results.length === 0) return;
      let next = this.highlightedIndex + direction;
      if (next < 0) next = this.results.length - 1;
      if (next >= this.results.length) next = 0;
      this.highlightedIndex = next;
    },
    selectHighlighted() {
      if (
        this.highlightedIndex >= 0 &&
        this.highlightedIndex < this.results.length
      ) {
        this.selectResult(this.results[this.highlightedIndex]);
      }
    },
    onBlur() {
      setTimeout(() => {
        this.showSuggestions = false;
      }, 100);
    },
    clear() {
      this.searchTerm = "";
      this.results = [];
      this.showSuggestions = false;
      this.$emit("address-cleared");
      this.$emit("update:clearInput", false);
    },
    onClearClick() {
      this.clear();
    },
  },
};
</script>

<style lang="scss" scoped>
.autocomplete-container {
  position: relative;
  width: 100%;

  .visually-hidden {
    position: absolute;

    overflow: hidden;

    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;

    white-space: nowrap;

    clip: rect(0, 0, 0, 0);
    border: 0;
  }

  .autocomplete-input-wrapper {
    position: relative;
    width: 100%;
  }

  .autocomplete-input {
    width: 100%;
    height: 48px;
    padding: 10px 32px 10px 10px;

    font-size: 16px;
    color: var(--search-filter-text-secondary-color);

    background-color: var(--search-filter-card-bg-color);
    border: 1px solid var(--search-filter-border-color);
    border-radius: 5px;
    outline: none;

    transition: border-color 0.2s ease;
  }

  .autocomplete-clear {
    cursor: pointer;

    position: absolute;
    top: 50%;
    right: 10px;
    transform: translateY(-50%);

    padding: 0 4px;

    font-size: 18px;
    line-height: 1;
    color: #888;

    background: transparent;
    border: none;
    border-radius: 50%;

    transition: background 0.2s;

    svg {
      fill: currentcolor;
    }

    &:hover svg {
      fill: var(--search-filter-text-color);
    }
  }
}

.autocomplete-items {
  position: absolute;
  z-index: 10;
  overflow-y: auto;
  width: 100%;
  max-height: 300px;
  margin-top: 5px;
  background-color: var(--search-filter-overlay-bg-color);
  border: 1px solid var(--search-filter-border-color);
  border-radius: 5px;
  box-shadow: 0 2px 6px rgb(0 0 0 / 10%);
  @include font-family("body");
}

.autocomplete-item {
  cursor: pointer;
  padding: 10px;
  transition: background-color 0.2s ease;
}

.autocomplete-item:hover,
.autocomplete-item.-selected {
  background-color: var(--search-filter-hover-color);
}
</style>
